import { PROJECTS } from "../../Constants/constants";
import { useEffect, useRef, useState } from "react";
import ProjectModal from "./ProjectModal.jsx";
import ProjectCard from "./ProjectCard.jsx";
import SectionTitle from "../SectionTitle.jsx";
import { useEvent } from "../../hooks/useEvent";
import { ANALYTICS_EVENTS } from "../../Constants/analyticsEvents";

// All projects live in one horizontal snap-scroll strip instead of an
// expandable grid: a "Show More" toggle changes the document height, which
// remaps every scroll position to a different frame of the full-page
// scroll-scrubbed background video. Horizontal overflow keeps the page
// height stable so the video timeline never shifts.
export default function Projects() {
  const [selectedProject, setSelectedProject] = useState(null);
  const stripRef = useRef(null);
  const track = useEvent();

  // Overflow containers don't drag with a mouse and vertical wheel gestures
  // scroll the page instead of the strip, so desktop users could never
  // reach the later cards. data-lenis-prevent keeps Lenis out of the strip;
  // these handlers add mouse-drag scrolling and convert wheel movement to
  // horizontal until the strip is exhausted, then let the page take over.
  useEffect(() => {
    const strip = stripRef.current;
    if (!strip) return;

    const onWheel = (e) => {
      const delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
      const atStart = strip.scrollLeft <= 0;
      const atEnd = strip.scrollLeft >= strip.scrollWidth - strip.clientWidth - 1;
      if ((delta > 0 && !atEnd) || (delta < 0 && !atStart)) {
        // stopPropagation, not just preventDefault: Lenis listens at the
        // window and would otherwise also process this wheel and scroll
        // the page over the strip's horizontal movement.
        e.preventDefault();
        e.stopPropagation();
        strip.scrollLeft += delta;
      }
    };

    let dragging = false;
    let dragMoved = false;
    let startX = 0;
    let startLeft = 0;

    const onPointerDown = (e) => {
      if (e.pointerType !== "mouse") return; // touch scrolls natively
      dragging = true;
      dragMoved = false;
      startX = e.clientX;
      startLeft = strip.scrollLeft;
    };
    const onPointerMove = (e) => {
      if (!dragging) return;
      const dx = e.clientX - startX;
      if (Math.abs(dx) > 5) dragMoved = true;
      strip.scrollLeft = startLeft - dx;
    };
    const endDrag = () => {
      dragging = false;
    };
    // A drag release lands on a card button — swallow that click so
    // dragging never opens a modal.
    const onClickCapture = (e) => {
      if (dragMoved) {
        e.preventDefault();
        e.stopPropagation();
        dragMoved = false;
      }
    };

    strip.addEventListener("wheel", onWheel, { passive: false });
    strip.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", endDrag);
    strip.addEventListener("click", onClickCapture, true);
    return () => {
      strip.removeEventListener("wheel", onWheel);
      strip.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", endDrag);
      strip.removeEventListener("click", onClickCapture, true);
    };
  }, []);

  const handleCloseModal = () => {
    setSelectedProject(null);
  };

  const handleProjectClick = (project) => {
    track(ANALYTICS_EVENTS.PROJECT_CLICK, { project: project.title });
    setSelectedProject(project);
  };

  return (
    <section id="projects">
      <div className="py-16">
        <SectionTitle>Projects</SectionTitle>

        <div
          ref={stripRef}
          data-lenis-prevent
          role="region"
          aria-label="Projects, scroll horizontally to explore"
          className="custom-scrollbar -mx-4 flex cursor-grab gap-6 overflow-x-auto px-4 pb-6 select-none active:cursor-grabbing lg:-mx-8 lg:px-8"
        >
          {PROJECTS.map((project, index) => (
            <div
              key={index}
              className="w-[260px] shrink-0 sm:w-[300px]"
            >
              <ProjectCard
                project={project}
                onClick={() => handleProjectClick(project)}
              />
            </div>
          ))}
        </div>
        <p className="text-slate-400 mt-2 text-center font-mono text-xs tracking-widest">
          ← SCROLL OR DRAG TO EXPLORE →
        </p>

        <ProjectModal project={selectedProject} onClose={handleCloseModal} />
      </div>
    </section>
  );
}
